import {
  and,
  eq,
  habit,
  habit_completion,
  habit_reminder,
  isNull,
  type PowerSyncDrizzleDB,
  streak_protector,
} from "@habits/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import {
  markRecentContentInsert,
  patchRowVerified,
  readMatchingRowVerifiedIfSuspect,
  type VerifiedWriteOptions,
} from "../powersync/verified-write";
import { isDayKey, serializeWeekdays } from "./schedule";
import type { CompletionStatus, HabitDraft } from "./types";

/**
 * Local mutations for habits domain. Writes to PowerSync SQLite;
 * the connector uploads them to the server (write-back) in the background.
 *
 * Every `UPDATE … WHERE id = ?` for `habit`, `habit_completion`, and
 * `habit_reminder` goes through `patchRowVerified`: a raw UPDATE against the PowerSync
 * view can match ZERO rows and return "success" while newly created row
 * is still in flight to server (see `../powersync/verified-write.ts`).
 * `habit_completion` is the daily record and streak counter — the most
 * costly damage in this product is precisely that: a streak user assumed
 * was saved and was not. Mutations **may throw**
 * (`LocalWriteLostError`): caller must report it, never swallow it.
 *
 * Excluded deliberately (see `../powersync/verified-write.ts` for rationale):
 * hard `DELETE`s and `spendStreakProtectorMutation` (row is granted by
 * server, never created by client).
 */

/** Sanitized target unit: trim, empty string → null. */
export function normalizeUnit(unit: string | null | undefined): string | null {
  const trimmed = unit?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : null;
}

// ─── Habits ──────────────────────────────────────────────────────────────────

export async function createHabitMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  draft: HabitDraft,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(habit).values({
    id,
    user_id: userId,
    name: draft.name.trim(),
    icon: draft.icon ?? null,
    color: draft.color ?? null,
    schedule_kind: draft.scheduleKind,
    weekdays: draft.weekdays?.length ? serializeWeekdays(draft.weekdays) : null,
    times_per_week: draft.scheduleKind === "times_per_week" ? (draft.timesPerWeek ?? 1) : null,
    sort_order: draft.sortOrder ?? 0,
    archived: 0,
    goal_kind: draft.goalKind ?? "binary",
    target_amount: draft.goalKind === "quantified" ? Math.max(1, draft.targetAmount ?? 1) : null,
    target_unit: draft.goalKind === "quantified" ? normalizeUnit(draft.targetUnit) : null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateHabitMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    name: string;
    icon: string | null;
    color: string | null;
    schedule_kind: string;
    weekdays: string | null;
    times_per_week: number | null;
    sort_order: number;
    archived: number;
    goal_kind: string;
    target_amount: number | null;
    target_unit: string | null;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.name !== undefined) {
    finalUpdates.name = finalUpdates.name.trim();
  }
  await patchRowVerified(db, habit, habit.id, id, finalUpdates, options);
}

/** Archiving preserves history; habit ceases to appear in "Today". */
export async function archiveHabitMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  archived: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await updateHabitMutation(db, id, { archived: archived ? 1 : 0 }, options);
}

/**
 * Deleting from UI = trash (L2): soft-delete. Habit disappears from
 * all views (queries filter `deleted_at IS NULL`) but its check-ins
 * and reminders ARE PRESERVED intact and reappear on restore.
 */
export async function trashHabitMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, habit, habit.id, id, { deleted_at: nowISO() }, options);
}

/** Restore from trash: habit returns with all its history. */
export async function restoreHabitMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, habit, habit.id, id, { deleted_at: null }, options);
}

/**
 * PERMANENT deletion (from trash, or inverse of "create habit"):
 * server cascades completions and reminders.
 */
export async function deleteHabitMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.delete(habit_reminder).where(eq(habit_reminder.habit_id, id));
  await db.delete(habit_completion).where(eq(habit_completion.habit_id, id));
  await db.delete(habit).where(eq(habit.id, id));
}

/** Persists manual order (complete list of ids in their new order). */
export async function reorderHabitsMutation(
  db: PowerSyncDrizzleDB,
  orderedIds: string[],
  options?: VerifiedWriteOptions,
): Promise<void> {
  for (const [index, id] of orderedIds.entries()) {
    await patchRowVerified(db, habit, habit.id, id, { sort_order: index }, options);
  }
}

// ─── Completions (the tap) ─────────────────────────────────────────────────

/** `habit_completion` row resolved by content key. */
interface CompletionRow {
  id: string;
  status: string | null;
  note: string | null;
  amount: number | null;
}

/** Content key for `habit_completion`, for recent INSERTs registry. */
function completionKey(habitId: string, dayKey: string): string {
  return `habit_completion:${habitId}:${dayKey}`;
}

/**
 * Resolves daily record for a (habit, day) by its CONTENT key
 * (`habit_id` + `date`) — no `id` known in advance, so this
 * SELECT decides whether mutations below execute INSERT or UPDATE. That
 * SELECT experiences the SAME phantom window as an `UPDATE … WHERE id = ?` (see
 * `../powersync/verified-write.ts`, "second class"): if row was created
 * moments earlier and checkpoint does not yet contain it, a raw SELECT would see
 * it absent and caller would attempt duplicate INSERT for same (habit_id,
 * date) — there is no `UNIQUE(habit_id, date)` index in local PowerSync
 * view to prevent it (client `Table`/`Index` schema lacks
 * the CONCEPT of a unique index — only Postgres declares it server-side:
 * `habit_completion_unique_day` in `00041_habits_core.sql` — and that round-trip
 * does not protect the instantaneous local read deciding INSERT-vs-UPDATE).
 *
 * `readMatchingRowVerifiedIfSuspect` retries the SAME query with backoff
 * before treating row as absent, but ONLY when there is reason to
 * suspect the window — see "Third piece" in `verified-write.ts`: if
 * this process did not just INSERT a row for that key, a single
 * read decides equally well without paying backoff penalty. Exhausting backoff (or
 * the solitary read without mark) does NOT throw: genuine absence (first time
 * day is tapped) is a valid result and caller proceeds to INSERT
 * as before.
 *
 * Single resolution point for `setCompletionMutation`,
 * `incrementCompletionAmountMutation`, and `setCompletionNoteMutation`: all three
 * needed this exact same SELECT+backoff, so it is extracted here
 * rather than repeated three times. The first two mark the key with
 * `markRecentContentInsert` right after their own INSERT — this way the
 * NEXT immediate tap on the same day pays the backoff (protected, as
 * before), but first tap of the day — the most common case in this domain —
 * no longer pays the ≈6.05 s full backoff to decide what a single
 * read answers just as well.
 */
async function resolveCompletionRow(
  db: PowerSyncDrizzleDB,
  habitId: string,
  dayKey: string,
  options?: VerifiedWriteOptions,
): Promise<CompletionRow | undefined> {
  return readMatchingRowVerifiedIfSuspect(
    completionKey(habitId, dayKey),
    () =>
      db
        .select({
          id: habit_completion.id,
          status: habit_completion.status,
          note: habit_completion.note,
          amount: habit_completion.amount,
        })
        .from(habit_completion)
        .where(and(eq(habit_completion.habit_id, habitId), eq(habit_completion.date, dayKey)))
        .limit(1),
    options,
  );
}

/**
 * Sets status for a day: creates/updates row if status, deletes if null.
 * `amount` (optional) records progress for a quantified habit.
 * Returns final status of day.
 */
export async function setCompletionMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  habitId: string,
  dayKey: string,
  status: CompletionStatus | null,
  note?: string | null,
  amount?: number | null,
  options?: VerifiedWriteOptions,
): Promise<CompletionStatus | null> {
  if (!isDayKey(dayKey)) {
    throw new Error(`Invalid day key: ${dayKey}`);
  }
  const current = await resolveCompletionRow(db, habitId, dayKey, options);

  const now = nowISO();
  if (status === null) {
    if (current) {
      await db.delete(habit_completion).where(eq(habit_completion.id, current.id));
    }
    return null;
  }
  if (current) {
    await patchRowVerified(
      db,
      habit_completion,
      habit_completion.id,
      current.id,
      {
        status,
        note: note === undefined ? current.note : note,
        amount: amount === undefined ? current.amount : amount,
      },
      options,
    );
  } else {
    await db.insert(habit_completion).values({
      id: generateId(),
      habit_id: habitId,
      user_id: userId,
      date: dayKey,
      status,
      note: note ?? null,
      amount: amount ?? null,
      created_at: now,
      updated_at: now,
    });
    // Marks the key: NEXT immediate tap on this (habitId, dayKey)
    // must pass through full backoff in `resolveCompletionRow` (see
    // its comment), because this newly created row may not have
    // landed in the view yet.
    markRecentContentInsert(completionKey(habitId, dayKey));
  }
  return status;
}

/**
 * Adds `delta` to progress of a quantified habit on a day.
 * - Day is always persisted with status='done' (streak interprets it as
 *   done only if amount >= target; see effectiveStatus).
 * - Clamped to [0, max] (max is usually the target; avoids overshoot).
 * - If resulting progress is 0, deletes row (complete unmark).
 * Returns new progress (null if deleted).
 */
export async function incrementCompletionAmountMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  habitId: string,
  dayKey: string,
  delta: number,
  max: number,
  options?: VerifiedWriteOptions,
): Promise<number | null> {
  if (!isDayKey(dayKey)) {
    throw new Error(`Invalid day key: ${dayKey}`);
  }
  const current = await resolveCompletionRow(db, habitId, dayKey, options);
  // A freeze does not accumulate numeric progress.
  const base = current && current.status !== "skipped" ? (current.amount ?? 0) : 0;
  const next = Math.max(0, Math.min(max, base + delta));
  const now = nowISO();
  if (next === 0) {
    if (current) {
      await db.delete(habit_completion).where(eq(habit_completion.id, current.id));
    }
    return null;
  }
  if (current) {
    await patchRowVerified(
      db,
      habit_completion,
      habit_completion.id,
      current.id,
      { status: "done", amount: next },
      options,
    );
  } else {
    await db.insert(habit_completion).values({
      id: generateId(),
      habit_id: habitId,
      user_id: userId,
      date: dayKey,
      status: "done",
      amount: next,
      note: null,
      created_at: now,
      updated_at: now,
    });
    // See same mark in `setCompletionMutation`: protects next
    // immediate tap without penalizing this one (first of the day).
    markRecentContentInsert(completionKey(habitId, dayKey));
  }
  return next;
}

/**
 * 1-tap cycle in "Today": unmarked → done → unmarked.
 * (skipped is set explicitly from habit menu.)
 */
export async function toggleCompletionMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  habitId: string,
  dayKey: string,
  current: CompletionStatus | null,
  options?: VerifiedWriteOptions,
): Promise<CompletionStatus | null> {
  const next = current === null ? "done" : null;
  return setCompletionMutation(db, userId, habitId, dayKey, next, undefined, undefined, options);
}

/** Day note (requires existing row; if nonexistent creates as done). */
export async function setCompletionNoteMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  habitId: string,
  dayKey: string,
  note: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const current = await resolveCompletionRow(db, habitId, dayKey, options);
  if (current) {
    await patchRowVerified(
      db,
      habit_completion,
      habit_completion.id,
      current.id,
      { note },
      options,
    );
  } else if (note) {
    await setCompletionMutation(db, userId, habitId, dayKey, "done", note, undefined, options);
  }
}

// ─── Day snapshot (for undo, L1) ────────────────────────────────────────────

/** Complete state of a (habit, day) — `null` = unmarked. */
export interface CompletionSnapshot {
  status: CompletionStatus;
  note: string | null;
  amount: number | null;
}

/**
 * Reads state BEFORE mutating a day: inverse of any check-in
 * mutation is "restore day as it was" (applyCompletionSnapshot).
 */
export async function getCompletionSnapshot(
  db: PowerSyncDrizzleDB,
  habitId: string,
  dayKey: string,
): Promise<CompletionSnapshot | null> {
  const rows = await db
    .select({
      status: habit_completion.status,
      note: habit_completion.note,
      amount: habit_completion.amount,
    })
    .from(habit_completion)
    .where(and(eq(habit_completion.habit_id, habitId), eq(habit_completion.date, dayKey)))
    .limit(1);
  const row = rows[0];
  if (!row) {
    return null;
  }
  return {
    status: row.status === "skipped" ? "skipped" : "done",
    note: row.note ?? null,
    amount: row.amount ?? null,
  };
}

/** Re-applies a snapshot via standard write path (inverse of check-ins). */
export async function applyCompletionSnapshot(
  db: PowerSyncDrizzleDB,
  userId: string,
  habitId: string,
  dayKey: string,
  snapshot: CompletionSnapshot | null,
): Promise<void> {
  if (snapshot === null) {
    await setCompletionMutation(db, userId, habitId, dayKey, null);
    return;
  }
  await setCompletionMutation(
    db,
    userId,
    habitId,
    dayKey,
    snapshot.status,
    snapshot.note,
    snapshot.amount,
  );
}

// ─── Recordatorios ───────────────────────────────────────────────────────────

export async function upsertReminderMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  habitId: string,
  timeOfDay: string,
  opts: { id?: string; enabled?: boolean } = {},
  options?: VerifiedWriteOptions,
): Promise<string> {
  const now = nowISO();
  if (opts.id) {
    await patchRowVerified(
      db,
      habit_reminder,
      habit_reminder.id,
      opts.id,
      { time_of_day: timeOfDay, enabled: opts.enabled === false ? 0 : 1 },
      options,
    );
    return opts.id;
  }
  const id = generateId();
  await db.insert(habit_reminder).values({
    id,
    habit_id: habitId,
    user_id: userId,
    time_of_day: timeOfDay,
    enabled: opts.enabled === false ? 0 : 1,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deleteReminderMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.delete(habit_reminder).where(eq(habit_reminder.id, id));
}

// ─── Streak protector ───────────────────────────────────────────────────────

/**
 * SPENDS an inventory protector on (habit, day). Local-first: writes to
 * SQLite and connector uploads it; server re-validates (window, ownership,
 * uniqueness) and cleanly discards invalid writes.
 *
 * Does NOT CREATE protectors: client cannot mint lives (granted by
 * server at `POST /api/habits/protectors/claim`). If inventory is empty
 * returns `null` and writes nothing.
 *
 * ⚠️ Caller must have verified BEFOREHAND, via `isDayProtectable`, that day
 * is rescuable against ACTIVE DEFINITION ON THAT DAY (interpretation sealing).
 *
 * Deliberately EXCLUDED from `patchRowVerified`: unlike `habit`,
 * `habit_completion`, and `habit_reminder`, client never performs an
 * `insert(streak_protector)` (search repo: none exists — only possible
 * `INSERT` would be minting lives, and `core-rust/src/db/streak_protector.rs`
 * prohibits it). Row ALWAYS arrives via sync stream after server-side
 * claim, so when `available()` displays it in UI it is already in
 * checkpoint — does not experience "recently created locally, not yet uploaded"
 * window that `patchRowVerified` fixes. Signal that would reopen it: a rescue
 * returning `null` (unspent) right after claiming a new protector in
 * same session, with inventory > 0 visible in UI.
 */
export async function spendStreakProtectorMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  habitId: string,
  dayKey: string,
): Promise<string | null> {
  if (!isDayKey(dayKey)) {
    return null;
  }
  const [next] = await db
    .select({ id: streak_protector.id })
    .from(streak_protector)
    .where(
      and(
        eq(streak_protector.user_id, userId),
        isNull(streak_protector.habit_id),
        isNull(streak_protector.deleted_at),
      ),
    )
    .limit(1);
  if (!next) {
    return null;
  }
  const now = nowISO();
  await db
    .update(streak_protector)
    .set({ habit_id: habitId, date: dayKey, applied_at: now, updated_at: now })
    .where(eq(streak_protector.id, next.id));
  return next.id;
}
